<template>
  <header class="topnav">
    <div class="topnav-bar">
      <!-- Prev / Next -->
      <div class="history-nav">
        <button type="button" class="history-btn" aria-label="Go back" @click="goBack">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M15 6l-6 6 6 6" />
          </svg>
        </button>
        <button type="button" class="history-btn" aria-label="Go forward" @click="goForward">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 6l6 6-6 6" />
          </svg>
        </button>
      </div>

      <div class="bar-divider"></div>

      <!-- Module search -->
      <div class="search-wrap">
        <svg class="search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" />
        </svg>
        <input
          v-model="searchQuery"
          type="text"
          class="search-input"
          placeholder="Search within modules..."
          @keyup.enter="submitSearch"
        />
        <button
          v-if="searchQuery"
          type="button"
          class="search-clear"
          aria-label="Clear search"
          @click="searchQuery = ''"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Right-aligned cluster -->
      <div class="topnav-right">
        <div class="bar-divider"></div>

        <!-- Notifications -->
        <button type="button" class="bell-btn" aria-label="Notifications">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.7 21a2 2 0 0 1-3.4 0" />
          </svg>
          <span class="bell-dot"></span>
        </button>

        <!-- User area -->
        <div class="user-menu">
          <button type="button" class="user-area" @click="toggleUserMenu">
            <span class="user-text">
              <span class="user-name">{{ fullName }}</span>
              <span class="user-role">{{ roleLabel }}</span>
            </span>
            <span class="avatar">{{ initials }}</span>
          </button>

          <transition name="user-menu-fade">
            <ul v-if="userMenuOpen" class="user-menu__dropdown" @click.stop>
              <li>
                <button type="button" class="user-menu__item" @click="handleLogout">
                  <i class="fa fa-right-from-bracket"></i>
                  <span>Log out</span>
                </button>
              </li>
            </ul>
          </transition>
        </div>
      </div>
    </div>
  </header>
</template>

<script>
export default {
  name: 'TopNavigation',

  data() {
    return {
      searchQuery: '',
      userMenuOpen: false,
      adminFirstName: localStorage.getItem('admin_first_name') || '',
      adminLastName: localStorage.getItem('admin_last_name') || '',
      adminRoleCode: localStorage.getItem('admin_role_code') || '',
    }
  },

  computed: {
    fullName() {
      const name = [this.adminFirstName, this.adminLastName].filter(Boolean).join(' ')
      return name || 'Admin User'
    },
    initials() {
      const parts = [this.adminFirstName, this.adminLastName].filter(Boolean)
      if (!parts.length) return 'A'
      return parts.map((p) => p[0]).slice(0, 2).join('').toUpperCase()
    },
    roleLabel() {
      // NOTE: backend returns a role `code` (e.g. 'admin', 'owner'), not a
      // display label — mapping shown here is a guess. Replace with your
      // actual role code -> label mapping, or have the backend return a
      // human-readable role name alongside the code.
      const map = {
        admin: 'Administrator',
        owner: 'Owner',
        manager: 'Manager',
      }
      return map[this.adminRoleCode] || this.adminRoleCode || 'Team Member'
    },
  },

  mounted() {
    document.addEventListener('click', this.closeUserMenu)
  },
  beforeUnmount() {
    document.removeEventListener('click', this.closeUserMenu)
  },

  methods: {
    goBack() {
      this.$emit('navigate-back')
    },
    goForward() {
      this.$emit('navigate-forward')
    },
    submitSearch() {
      this.$emit('search', this.searchQuery)
    },
    toggleUserMenu() {
      this.userMenuOpen = !this.userMenuOpen
    },
    closeUserMenu() {
      this.userMenuOpen = false
    },
    handleLogout() {
      localStorage.removeItem('admin_token')
      localStorage.removeItem('admin_id')
      localStorage.removeItem('admin_external_id')
      localStorage.removeItem('admin_first_name')
      localStorage.removeItem('admin_last_name')
      localStorage.removeItem('admin_email')
      localStorage.removeItem('admin_company_id')
      localStorage.removeItem('admin_company_name')
      localStorage.removeItem('admin_role_code')
      this.$router.push({ name: 'AdminLogin' })
    },
  },
}
</script>
<style scoped>
.topnav {
  width: 100%;
  height: 76px;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 0 24px;
  background: var(--color-surface);
  border-bottom: 1px solid var(--color-border);
  box-sizing: border-box;
  font-family: 'Inter', 'Segoe UI', -apple-system, BlinkMacSystemFont, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.topnav * {
  box-sizing: border-box;
}

.topnav-brand {
  flex-shrink: 0;
}

.brand-mark {
  width: 38px;
  height: 38px;
  border-radius: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-surface);
  background: linear-gradient(145deg, var(--color-primary), var(--color-secondary));
  box-shadow: 0 4px 10px rgba(93, 28, 106, 0.28);
}
.brand-mark svg {
  width: 20px;
  height: 20px;
}

/* ==========================================================================
   2. Rounded pill bar
   ========================================================================== */
.topnav-bar {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  height: 52px;
  background: var(--color-tint-light);
  border: 1px solid var(--color-border);
  border-radius: 999px;
  padding: 6px 8px 6px 8px;
  gap: 6px;
}

.bar-divider {
  width: 1px;
  height: 24px;
  background: var(--color-border);
  flex-shrink: 0;
}

/* Prev / next history controls */
.history-nav {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.history-btn {
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
}
.history-btn svg {
  width: 17px;
  height: 17px;
}
.history-btn:hover {
  background: var(--color-surface);
  color: var(--color-secondary);
  box-shadow: 0 4px 10px -4px rgba(93, 28, 106, 0.3);
}
.history-btn:active {
  transform: scale(0.94);
}

/* Module search */
.search-wrap {
  flex: 1;
  min-width: 0;
  max-width: 400px;
  height: 39px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  background: var(--color-surface);
  border: 1px solid #e0d9d9;
  border-radius: 15px;
}

.search-icon {
  width: 17px;
  height: 17px;
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.search-input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-size: 13.5px;
  font-weight: 500;
  color: var(--color-text-dark);
  font-family: inherit;
}
.search-input::placeholder {
  color: var(--color-text-muted);
  font-weight: 400;
}

.search-clear {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: rgba(93, 28, 106, 0.08);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.18s ease, color 0.18s ease;
}
.search-clear svg {
  width: 12px;
  height: 12px;
}
.search-clear:hover {
  background: var(--color-secondary);
  color: #fff;
}
.topnav-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}

/* Notifications */
.bell-btn {
  position: relative;
  width: 38px;
  height: 38px;
  border: none;
  border-radius: 50%;
  background: var(--color-surface);
  color: var(--color-text-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.2s ease, color 0.2s ease, transform 0.15s ease;
}
.bell-btn svg {
  width: 18px;
  height: 18px;
}
.bell-btn:hover {
  color: var(--color-secondary);
  box-shadow: 0 4px 10px -4px rgba(93, 28, 106, 0.3);
}
.bell-btn:active {
  transform: scale(0.94);
}

.bell-dot {
  position: absolute;
  top: 7px;
  right: 8px;
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: var(--color-secondary);
  border: 2px solid var(--color-surface);
}

/* User area */
.user-area {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border: none;
  background: var(--color-surface);
  border-radius: 999px;
  padding: 6px 15px 6px 15px;
  cursor: pointer;
  transition: box-shadow 0.2s ease, transform 0.15s ease;
}
.user-area:hover {
  box-shadow: 0 6px 16px -6px rgba(93, 28, 106, 0.35);
}
.user-area:active {
  transform: scale(0.98);
}

.user-text {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.25;
}

.user-name {
  font-size: 13px;
  font-weight: 700;
  color: var(--color-text-dark);
}

.user-role {
  font-size: 11px;
  font-weight: 500;
  color: var(--color-text-muted);
}

.avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: linear-gradient(145deg, var(--color-secondary), var(--color-tint-warm));
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.3px;
  box-shadow: 0 4px 10px rgba(202, 89, 149, 0.4);
  flex-shrink: 0;
}

/* ==========================================================================
   3. Responsive
   ========================================================================== */
@media (max-width: 900px) {
  .user-text {
    display: none;
  }
  .user-area {
    padding: 4px;
  }
}

@media (max-width: 640px) {
  .topnav {
    padding: 0 14px;
    height: 68px;
    gap: 10px;
  }
  .topnav-brand {
    display: none;
  }
  .topnav-bar {
    height: 46px;
    padding: 4px;
  }
  .history-btn {
    width: 34px;
    height: 34px;
  }
  .search-wrap {
    padding: 0 8px;
  }
  .avatar {
    width: 30px;
    height: 30px;
  }
}

@media (max-width: 420px) {
  .history-nav {
    display: none;
  }
}

.user-menu {
  position: relative;
}

.user-menu__dropdown {
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  min-width: 160px;
  margin: 0;
  padding: 6px;
  list-style: none;
  border-radius: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  box-shadow: 0 16px 34px -14px rgba(12, 18, 83, 0.3);
  z-index: 40;
}

.user-menu__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 36px;
  padding: 0 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  font-size: 12.5px;
  font-weight: 600;
  color: var(--color-text-dark);
  cursor: pointer;
  transition: background 0.15s ease;
}
.user-menu__item:hover {
  background: var(--color-tint-light);
}

.user-menu-fade-enter-active,
.user-menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.user-menu-fade-enter-from,
.user-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>